import type {JSONRequestInit} from '@github-ui/verified-fetch'
import {reactFetchJSON} from '@github-ui/verified-fetch'

/**
 *
 * Use when catching a "TypeError: Failed to fetch" error.
 * Typically happens when the fetch request catches one of the following:
 * - CORS
 * - Browser Extensions are calling or modifying the response
 * - Incorrect or Incomplete URL
 * - Incorrect or Incomplete Headers, URL or HTTP Methods
 * - Or when the request is blocked by the browser's Content Security Policy (CSP)
 */
export class FetchRequestError extends Error {
  constructor(cause: unknown) {
    super('Unable to perform this operation. Please try again later.')
    this.name = 'FetchRequestError'
    this.cause = cause
  }
}

/**
 *
 * Use when server returns a 500 error (e.g. server is unavailable)
 */
export class ServerUnavailableError extends Error {
  constructor(cause: unknown) {
    super('Unable to perform this operation. Please try again later.')
    this.name = 'ServerUnavailableError'
    this.cause = cause
  }
}

/**
 *
 * Use when server returns invalid json.
 * e.g.
 * - JSON.parse: unexpected character at line 1 column 1 of the JSON data
 * - <HTML> or <DOCTYPE> is returned
 */
export class JSONParseFetchError extends Error {
  constructor(cause: unknown) {
    super('Unable to read response from the server. Please try again later.')
    this.name = 'JSONParseFetchError'
    this.cause = cause
  }
}

/** Used when a user's SSO session expires and we fail to return the previous data. */
export class AuthSessionExpiredError extends Error {
  constructor() {
    super('Unable to perform this operation. Please try again later.')
    this.name = 'AuthSessionExpiredError'
  }
}

/** Used when the user tries to submit content that has already changed. */
export class StaleContentError<Data> extends Error {
  /** Fresh data from the server, can be used to update the query store to show the updated changes. */
  readonly freshData: Data

  constructor(freshData: Data) {
    super('The content you are editing has changed. Please review the latest changes and try again.')
    this.name = 'StaleContentError'
    this.freshData = freshData
  }
}

/**
 *
 * @param response Response
 * @returns Promise<any>
 * @throws {JSONParseFetchError} if it's unable to parse JSON from the response
 *
 * Use to wrap JSON parsing from HTTP response with better error messages.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function parseJSONWithBetterErrors(response: Response): Promise<any> {
  // Attempt to parse JSON, but if it fails, throw a generic error
  try {
    return await response.json()
  } catch (e) {
    // We don't want to fail if an Abort Signal stopped the response originally
    if (e instanceof Error && e.name === 'AbortError') return undefined

    throw new JSONParseFetchError(e)
  }
}

/**
 *
 * @param response Response
 * @param json object
 * @param predefinedError Error
 * @returns void
 * @throws {ServerUnavailableError} if response has a 5xx status.
 * @throws {predefinedError} if provided and response status is not a 5xx.
 * @throw {Error} using json values if json is passed without predefinedError or response is not a 5xx status.
 * @throw Error with response.status if none of the above.
 */
export function throwErrorsIfBadResponse(
  response: Response,
  json?: {error?: string} | object,
  predefinedError?: Error,
): void {
  if (response.ok) return

  if (response.status >= 500) {
    throw new ServerUnavailableError(response.status)
  }

  if (predefinedError) {
    throw predefinedError
  }

  if (json) {
    const error = 'error' in json ? json.error : undefined
    throw new Error(error || 'Unknown error occurred', {cause: response.status})
  }

  throw new Error(`HTTP ${response.status}`)
}

/**
 * For endpoints that support stale data detection before performing mutations. Throws an error if `stale` is present
 * and truthy in the response (and the response is not a 2xx status).
 * @throws {StaleContentError<Data>} If the response indicates stale data. `freshData` field will be set to passed `json`.
 */
export function throwIfStaleResponse<Data extends {stale: true}>(response: Response, json?: Data | object): void {
  if (!response.ok && json && 'stale' in json && json.stale) throw new StaleContentError<Data>(json)
}

/**
 *
 * @param path string
 * @param init JSONRequestInit
 * @returns Response
 *
 * Use when you want to make a fetch request.
 * This function wraps reactFetchJSON and handles unsafe fetching
 */
export async function fetchWithErrorHandling(path: string, init?: JSONRequestInit): Promise<Response> {
  try {
    return await reactFetchJSON(path, init)
  } catch (e) {
    throw new FetchRequestError(e)
  }
}
